// Ease & Co add-to-cart popup. Ported from the Carmoods cart notification:
// #cart-notification is a <dialog> opened with showModal(), closes on Esc, backdrop click
// and [data-cart-notification-close], and restores focus. The summary strip reads its label
// from [data-cart-summary-label] in the re-rendered cart-notification-button section, and the
// header .eco-count is updated from its data-cart-count.
class CartNotification extends HTMLElement {
  constructor() {
    super();
    this.notification = this.querySelector('#cart-notification');
    if (!this.notification) return;

    this.header = document.querySelector('sticky-header');
    this.notification.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.notification.addEventListener('close', () => this.finishClose());
    this.notification.addEventListener('click', (event) => this.handleBackdropClick(event));
    this.querySelectorAll('[data-cart-notification-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });
  }

  open() {
    if (!this.notification) return;
    if (!this.notification.open) {
      if (!this.activeElement || !this.activeElement.isConnected) this.activeElement = document.activeElement;
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      this.notification.showModal();
    }
    document.body.classList.add('eco-cart-notification-open');
    this.notification.classList.add('active');
    this.notification.focus({ preventScroll: true });
  }

  close() {
    if (!this.notification || !this.notification.open) return;
    this.notification.classList.remove('active');
    this.notification.close();
    // Chrome does not always fire 'close' when close() runs inside a 'cancel' (Esc)
    // handler, which left the scroll lock on. finishClose() is safe to run twice.
    this.finishClose();
  }

  finishClose() {
    if (this.notification.open) return;
    document.body.classList.remove('eco-cart-notification-open');
    this.notification.classList.remove('active');
    if (this.activeElement && this.activeElement.isConnected) this.activeElement.focus({ preventScroll: true });
    this.activeElement = null;
  }

  disconnectedCallback() {
    if (this.notification && this.notification.open) {
      this.notification.close();
      this.finishClose();
    }
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key || (parsedState.items && parsedState.items[0] && parsedState.items[0].key);
    const summary = this.querySelector('[data-cart-notification-summary]');
    if (summary) summary.hidden = true;
    let count = null;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      if (!sectionElement) return;
      const html = this.getSectionInnerHTML(parsedState.sections && parsedState.sections[section.id], section.selector);
      if (html === null) return;
      sectionElement.innerHTML = html;

      if (section.id === 'cart-notification-button') {
        const labelSource = sectionElement.querySelector('[data-cart-summary-label]');
        if (labelSource) {
          const parsed = parseInt(labelSource.getAttribute('data-cart-count'), 10);
          if (!isNaN(parsed)) count = parsed;
          const label = summary && summary.querySelector('[data-cart-notification-summary-text]');
          if (label) {
            label.textContent = labelSource.getAttribute('data-cart-summary-label');
            summary.hidden = false;
          }
        }
      }
    });

    if (count !== null) {
      this.updateHeaderCount(count);
    } else {
      this.refreshHeaderCount();
    }

    if (this.header) this.header.reveal();
    this.open();
  }

  updateHeaderCount(count) {
    document.querySelectorAll('.eco-count').forEach((el) => {
      el.textContent = count > 99 ? '99+' : count;
      el.hidden = count < 1;
    });
  }

  refreshHeaderCount() {
    fetch(`${(window.routes && window.routes.cart_url) || '/cart'}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => this.updateHeaderCount(cart.item_count))
      .catch(() => {});
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : null;
  }

  handleBackdropClick(event) {
    if (event.target !== this.notification) return;
    const rect = this.notification.getBoundingClientRect();
    if (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    ) {
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-notification')) customElements.define('cart-notification', CartNotification);
